// Audit log (/settings/audit-log) — List archetype (UI-LAW §4c) at config
// width: one chrome row (three pick-from-a-list filters + the CSV export)
// then the entries table.
//
// Every filter is a closed set the reader can recognise on sight — a team
// member, a resource kind, a date range — never a free-text box asking them
// to already know a uuid or the internal spelling of a resource type. That
// makes Apply/Clear unnecessary: a pick IS the filter, so each control
// navigates on change and the URL stays the applied state (§4c "URL is UI
// state": the route's validateSearch owns them, so a filtered view survives
// refresh and is shareable). The applied values ride the query key, so each
// change is a fresh keyset stream.
//
// `resource_id` remains a supported API filter with no control here: it is
// reachable by deep link (and by the row's copy affordance) but it is not
// something a person arrives at this screen already holding.

import { useState } from "react";

import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import type { components, operations } from "../api/schema";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { formatDateTime } from "../lib/format";
import { DEFAULT_PAGE_SIZE } from "../lib/pagination";
import { queryKeys } from "../lib/queryKeys";
import { parseAuditLogSearch, type AuditLogSearch } from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useListQuery, type ListQueryFilters } from "../lib/useListQuery";
import {
  Badge,
  Button,
  Code,
  Copyable,
  DateRangePicker,
  downloadCsv,
  EmptyState,
  InlineError,
  KeyValue,
  Page,
  PageHeader,
  Select,
  ShellIcon,
  Stack,
  Text,
} from "../ui";
import type { DateRange } from "../ui";
import { PaginatedTableSection } from "./shared/PaginatedTableSection";
import { NameWithId } from "./shared/NameWithId";

type AuditLog = components["schemas"]["AuditLogResponse"];
type AuditLogListFilters = ListQueryFilters<"/v1/audit-logs">;

// The applied filter values, before pagination is layered on. `GET
// /v1/audit-logs` takes these as query params (and gains an `action` filter in
// a later frontend wave); the export route that once shared this shape is gone.
interface AuditLogFilterValues {
  actor_id: string | null;
  occurred_after: string | null;
  occurred_before: string | null;
  resource_id: string | null;
  resource_type: string | null;
}
type User = components["schemas"]["UserResponse"];
type UserListQuery = NonNullable<
  operations["usersList"]["parameters"]["query"]
>;

interface FilterDraft {
  actor_id: string;
  occurred: DateRange;
  resource_type: string;
}

// The occurred range uses the UI-LAW §4 DateRangePicker; the other two are
// selects over a closed set, so their change handler stays string-typed and
// cannot target `occurred`.
type StringFilterField = "actor_id" | "resource_type";

export interface AuditLogSettingsViewProps {
  auditLogs: AuditLog[];
  filters: FilterDraft;
  hasMoreAuditLogs: boolean;
  isAuditLogsFetchingNextPage: boolean;
  isAuditLogsPending: boolean;
  listErrorMessage?: string;
  listStateKey: string;
  onClearFilters: () => void;
  onFetchNextAuditLogs: () => void;
  onFilterChange: (field: StringFilterField, value: string) => void;
  onOccurredRangeChange: (range: DateRange) => void;
  users: User[];
  usersById: ReadonlyMap<string, User>;
  usersErrorMessage?: string;
}

const emptyFilterDraft: FilterDraft = {
  actor_id: "",
  occurred: { end: null, start: null },
  resource_type: "",
};

const firstPageToken: string | undefined = undefined;

// Every resource kind the audit log records, labelled the way the product
// names it. The value is the API's `resource_type` token; the label is what a
// reader would call the thing, because nobody arrives here knowing that a
// Marker's spans are stored as "trace".
const auditLogResourceTypes = [
  { label: "Agent", value: "agent" },
  { label: "Agent version", value: "agent_version" },
  { label: "Answer", value: "answer" },
  { label: "API key", value: "api_key" },
  { label: "Audit log", value: "audit_log" },
  { label: "Batch", value: "batch" },
  { label: "Connection", value: "connection" },
  { label: "Marker", value: "marker" },
  { label: "Membership", value: "membership" },
  { label: "Note", value: "note" },
  { label: "Organization", value: "organization" },
  { label: "Persona", value: "persona" },
  { label: "Retention policy", value: "retention_policy" },
  { label: "Scenario", value: "scenario" },
  { label: "Scenario set", value: "scenario_set" },
  { label: "Team", value: "team" },
  { label: "Template", value: "template" },
  { label: "Trace", value: "trace" },
  { label: "Transcript", value: "transcript" },
  { label: "User", value: "app_user" },
  { label: "Variable", value: "variable" },
] as const;

function resourceTypeLabel(resourceType: string): string {
  return (
    auditLogResourceTypes.find((entry) => entry.value === resourceType)
      ?.label ?? resourceType
  );
}

function nullableFilter(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function userMap(users: User[]): ReadonlyMap<string, User> {
  return new Map(users.map((user) => [user.id, user]));
}

function usersListQuery(): UserListQuery {
  return { page_size: DEFAULT_PAGE_SIZE, page_token: firstPageToken };
}

function userDisplayName(user: User): string {
  const displayName = user.display_name?.trim();
  return displayName && displayName.length > 0 ? displayName : user.email;
}

// One line per row. A name-over-email stack made every row taller than the
// 40px the virtualized table windows on (UI-LAW §3), which misaligns the
// spacer padding; the email stays reachable as the cell's title.
function UserLabel({ fallbackId, user }: { fallbackId: string; user?: User }) {
  if (!user) {
    return <Copyable label="User ID" value={fallbackId} />;
  }

  const name = userDisplayName(user);
  return (
    <Text
      as="span"
      title={name === user.email ? name : `${name} (${user.email})`}
      truncate
    >
      {name}
    </Text>
  );
}

// The UI-LAW §4 DateRangePicker is day-granular; `GET /v1/audit-logs` takes both
// bounds as RFC3339 `date-time`. Widen each picked day to the whole local day so
// the range is inclusive of every event on it: the start day opens at 00:00:00,
// the end day closes at 23:59:59.999. `toISOString()` emits a valid RFC3339 Z
// instant for that local boundary.
function startOfDayIso(date: Date): string {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
  ).toISOString();
}

function endOfDayIso(date: Date): string {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  ).toISOString();
}

// `resourceId` is carried through from the URL: the screen has no control for
// it, so a deep-linked one must survive every other filter change.
function draftToFilters(
  draft: FilterDraft,
  resourceId: string | null,
): AuditLogFilterValues {
  return {
    actor_id: nullableFilter(draft.actor_id),
    occurred_after: draft.occurred.start
      ? startOfDayIso(draft.occurred.start)
      : null,
    occurred_before: draft.occurred.end
      ? endOfDayIso(draft.occurred.end)
      : null,
    resource_id: resourceId,
    resource_type: nullableFilter(draft.resource_type),
  };
}

// URL search params → the applied filter values (absent = null).
function searchToFilters(search: AuditLogSearch): AuditLogFilterValues {
  return {
    actor_id: search.actor_id ?? null,
    occurred_after: search.occurred_after ?? null,
    occurred_before: search.occurred_before ?? null,
    resource_id: search.resource_id ?? null,
    resource_type: search.resource_type ?? null,
  };
}

// Applied filters → the search-param patch Apply/Clear navigates with.
// `undefined` drops a cleared key from the URL under the functional merge.
function filtersToSearchPatch(filters: AuditLogFilterValues): AuditLogSearch {
  return {
    actor_id: filters.actor_id ?? undefined,
    occurred_after: filters.occurred_after ?? undefined,
    occurred_before: filters.occurred_before ?? undefined,
    resource_id: filters.resource_id ?? undefined,
    resource_type: filters.resource_type ?? undefined,
  };
}

// Rehydrate the filter-bar draft from the URL on mount, so a deep-linked or
// refreshed filtered view shows its own filters in the bar. The applied
// occurred bounds land inside their day (start-of-day / end-of-day instants),
// so `new Date(iso)` recovers the picked day for the day-granular picker.
function draftFromSearch(search: AuditLogSearch): FilterDraft {
  return {
    actor_id: search.actor_id ?? "",
    occurred: {
      end: search.occurred_before ? new Date(search.occurred_before) : null,
      start: search.occurred_after ? new Date(search.occurred_after) : null,
    },
    resource_type: search.resource_type ?? "",
  };
}

// The path's own query filters, minus pagination (useListQuery owns those).
// `undefined` values are omitted from the wire by the client's serializer.
function filtersToListParams(
  filters: AuditLogFilterValues,
): AuditLogListFilters {
  return {
    actor_id: filters.actor_id ?? undefined,
    occurred_after: filters.occurred_after ?? undefined,
    occurred_before: filters.occurred_before ?? undefined,
    resource_id: filters.resource_id ?? undefined,
    resource_type: filters.resource_type ?? undefined,
  };
}

function nullCell() {
  return (
    <Text as="span" variant="muted">
      —
    </Text>
  );
}

function formatJsonBlock(value: unknown): string {
  if (value == null) {
    return "—";
  }

  return JSON.stringify(value, null, 2);
}

// The row's overflow: the fields nobody scans a table by, behind a disclosure.
// A dash in a row means the entry genuinely carries no such value (a create
// has no prior revision; a machine-principal call has no IP) — the row is
// rendered, not missing.
function AuditLogDetails({ entry }: { entry: AuditLog }) {
  return (
    <details className="marker-audit-log-details">
      <summary className="marker-audit-log-details__summary">
        <span aria-hidden="true" className="marker-audit-log-details__marker">
          <ShellIcon name="chevronDown" />
        </span>
        Details
      </summary>
      <Stack className="marker-audit-log-details__body" gap={2}>
        <KeyValue
          rows={[
            {
              label: "Request ID",
              value: entry.request_id ? (
                <Copyable label="Request ID" value={entry.request_id} />
              ) : (
                nullCell()
              ),
            },
            {
              label: "Version ID",
              value: entry.revision_id ? (
                <Copyable label="Version ID" value={entry.revision_id} />
              ) : (
                nullCell()
              ),
            },
            {
              label: "IP",
              value: entry.ip ? <Code>{entry.ip}</Code> : nullCell(),
            },
          ]}
        />
        {entry.diff ? <Code block>{formatJsonBlock(entry.diff)}</Code> : null}
      </Stack>
    </details>
  );
}

function auditLogColumns(
  usersById: ReadonlyMap<string, User>,
): Array<ColumnDef<AuditLog>> {
  return [
    {
      // accessorKey makes this UI-LAW §4 DataTable column sortable over the
      // loaded rows; ISO 8601 strings sort chronologically.
      accessorKey: "occurred_at",
      header: "Occurred",
      cell: ({ row }) => (
        <Text as="span">{formatDateTime(row.original.occurred_at)}</Text>
      ),
    },
    {
      accessorKey: "action",
      header: "Action",
      cell: ({ row }) => <Badge>{row.original.action}</Badge>,
    },
    {
      // The kind reads as a product noun ("Retention policy"), never the wire
      // token; the id stays a hover-reveal copy affordance beside it. Bounded
      // so this column cannot push Actor off to the right.
      header: "Resource",
      id: "resource",
      enableSorting: false,
      cell: ({ row }) => (
        <NameWithId
          id={row.original.resource_id}
          idLabel="Resource ID"
          name={resourceTypeLabel(row.original.resource_type)}
          truncate
        />
      ),
    },
    {
      header: "Actor",
      id: "actor",
      accessorFn: (entry) => {
        if (!entry.actor_id) {
          return "system";
        }
        const user = usersById.get(entry.actor_id);
        return user
          ? `${userDisplayName(user)} <${user.email}>`
          : entry.actor_id;
      },
      enableSorting: false,
      cell: ({ row }) => {
        const actorId = row.original.actor_id;
        return actorId ? (
          <UserLabel fallbackId={actorId} user={usersById.get(actorId)} />
        ) : (
          <Text as="span">system</Text>
        );
      },
    },
    {
      header: "",
      id: "details",
      enableSorting: false,
      cell: ({ row }) => <AuditLogDetails entry={row.original} />,
    },
  ];
}

export function AuditLogSettings() {
  const navigate = useTypedNavigate<AuditLogSearch>();
  const search = useTypedSearch(parseAuditLogSearch);
  const [filterDraft, setFilterDraft] = useState<FilterDraft>(() =>
    draftFromSearch(search),
  );
  // The URL is the applied-filter state (§4c). Same-route search writes are
  // ALWAYS functional merges so the root `?agent` scope survives; an unbound
  // navigate cannot type this route's search schema (see lib/workingScope.ts),
  // so the args are typed loosely — the merge holds.
  const appliedFilters = searchToFilters(search);

  function patchSearch(patch: AuditLogSearch): void {
    void navigate({
      replace: true,
      search: mergeSearch<AuditLogSearch>(patch),
    });
  }

  // The applied filters ride inside the query key (a filter change is a fresh
  // keyset stream, never an offset synthesis).
  const auditLogListParams = filtersToListParams(appliedFilters);
  const auditLogs = useListQuery("/v1/audit-logs", {
    params: auditLogListParams,
    queryKey: queryKeys.auditLogs.list(auditLogListParams),
  });

  // One directory read serves both the actor column AND the team-member
  // filter: the filter needs the whole tenant-visible roster (you pick a
  // person who may have no entry on the loaded page), which is a superset of
  // the ids the loaded rows reference.
  const users = useQuery({
    queryKey: queryKeys.users.list({ surface: "audit-log-actors" }),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/users", {
        params: { query: usersListQuery() },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  // A pick IS the filter: every control applies on change, so there is no
  // Apply button and no draft/applied split to explain.
  function applyDraft(draft: FilterDraft): void {
    setFilterDraft(draft);
    patchSearch(
      filtersToSearchPatch(draftToFilters(draft, appliedFilters.resource_id)),
    );
  }

  function clearFilters(): void {
    setFilterDraft(emptyFilterDraft);
    patchSearch(filtersToSearchPatch(draftToFilters(emptyFilterDraft, null)));
  }

  function changeFilter(field: StringFilterField, value: string): void {
    applyDraft({ ...filterDraft, [field]: value });
  }

  function changeOccurredRange(range: DateRange): void {
    applyDraft({ ...filterDraft, occurred: range });
  }

  return (
    <AuditLogSettingsView
      auditLogs={auditLogs.items}
      filters={filterDraft}
      hasMoreAuditLogs={auditLogs.hasNextPage}
      isAuditLogsFetchingNextPage={auditLogs.isFetchingNextPage}
      isAuditLogsPending={auditLogs.isPending}
      listErrorMessage={auditLogs.errorMessage}
      listStateKey={auditLogs.listStateKey}
      onClearFilters={clearFilters}
      onFetchNextAuditLogs={auditLogs.fetchNextPage}
      onFilterChange={changeFilter}
      onOccurredRangeChange={changeOccurredRange}
      users={users.data?.items ?? []}
      usersById={userMap(users.data?.items ?? [])}
      usersErrorMessage={users.isError ? users.error.message : undefined}
    />
  );
}

export function AuditLogSettingsView({
  auditLogs,
  filters,
  hasMoreAuditLogs,
  isAuditLogsFetchingNextPage,
  isAuditLogsPending,
  listErrorMessage,
  listStateKey,
  onClearFilters,
  onFetchNextAuditLogs,
  onFilterChange,
  onOccurredRangeChange,
  users,
  usersById,
  usersErrorMessage,
}: AuditLogSettingsViewProps) {
  const columns = auditLogColumns(usersById);
  const hasFilters =
    filters.actor_id !== "" ||
    filters.resource_type !== "" ||
    filters.occurred.start != null ||
    filters.occurred.end != null;

  // Export what the reader is looking at: the loaded rows under the applied
  // filters, in the same order and with the same resolved labels as the table.
  function exportCsv(): void {
    downloadCsv("audit-log.csv", [
      ["Occurred", "Action", "Resource", "Resource ID", "Actor", "Request ID"],
      ...auditLogs.map((entry) => {
        const actor = entry.actor_id
          ? usersById.get(entry.actor_id)
          : undefined;
        return [
          entry.occurred_at,
          entry.action,
          resourceTypeLabel(entry.resource_type),
          entry.resource_id,
          entry.actor_id
            ? actor
              ? userDisplayName(actor)
              : entry.actor_id
            : "system",
          entry.request_id ?? "",
        ];
      }),
    ]);
  }

  return (
    <Page variant="workspace">
      {/* One chrome row: the three pickers on the left, the export on the
          right. No Apply — each control navigates on change. §4b.3 keeps the
          row fixed while the table below owns the remaining height and its
          own scroll, so the filters and the column headers stay put. */}
      <PageHeader
        actions={
          <Button
            disabled={auditLogs.length === 0}
            size="sm"
            type="button"
            variant="secondary"
            onClick={exportCsv}
          >
            Export CSV
          </Button>
        }
        compact
        leading={
          <>
            <Select
              label="Team member"
              labelHidden
              size="sm"
              value={filters.actor_id}
              onChange={(event) => {
                onFilterChange("actor_id", event.currentTarget.value);
              }}
            >
              <option value="">All team members</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {userDisplayName(user)}
                </option>
              ))}
            </Select>
            <Select
              label="Resource"
              labelHidden
              size="sm"
              value={filters.resource_type}
              onChange={(event) => {
                onFilterChange("resource_type", event.currentTarget.value);
              }}
            >
              <option value="">All resources</option>
              {auditLogResourceTypes.map((resourceType) => (
                <option key={resourceType.value} value={resourceType.value}>
                  {resourceType.label}
                </option>
              ))}
            </Select>
            {/* No visible label: in a chrome row the placeholder already
                reads "Any date", and a caps label above would push the row
                off the sm control height its siblings sit at. */}
            <DateRangePicker
              ariaLabel="Occurred date range"
              placeholder="Any date"
              value={filters.occurred}
              onChange={onOccurredRangeChange}
            />
            {hasFilters ? (
              <Button
                size="sm"
                type="button"
                variant="secondary"
                onClick={onClearFilters}
              >
                Clear
              </Button>
            ) : null}
          </>
        }
      />
      {usersErrorMessage ? (
        <InlineError>{usersErrorMessage}</InlineError>
      ) : null}
      <PaginatedTableSection
        ariaLabel="Audit log"
        boundedColumns={["resource"]}
        className="marker-data-table--narrow-bounds"
        columns={columns}
        emptyState={<EmptyState title="No audit log entries" />}
        enableSorting
        errorMessage={listErrorMessage}
        getRowId={(auditLog) => auditLog.id}
        hasNextPage={hasMoreAuditLogs}
        isFetchingNextPage={isAuditLogsFetchingNextPage}
        isPending={isAuditLogsPending}
        items={auditLogs}
        listStateKey={listStateKey}
        skeletonLabel="Loading audit log"
        skeletonRows={3}
        virtual={{ fill: true }}
        onLoadMore={onFetchNextAuditLogs}
      />
    </Page>
  );
}
